/**
 * @description - Routes variable for different platforms.
 * Contains list of RouteItem type objects that was passed to
 * renderRoutes method for Switch component
 */

// ================================================================================================================== //
// ===================================================== MODULES ==================================================== //
// ================================================================================================================== //

// Lazy loading is the technique of rendering only-needed or
// critical user interface items first, then quietly unrolling the non-critical
// items later. It is now fully integrated into core react library itself.
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
// Paths
import { PATH_APP_DOCS, PATH_APP_AUTH, PATH_APP_MAIN } from 'src/routes';
// Types
import { TypeRouteItem } from 'src/@types';
// Components
import { Loadable, AuthGuard } from 'src/components';
// Layouts
import { DashboardLayout, MainLayout } from 'src/layouts';
// Navigation configuration
import { docsNavBarConfig } from 'src/routes';
import DocEditView from "src/pages/docs/views/DocEditViewDevice";
import DocsCreateEditTemplateRTView
  from "../views/DocsCreateEditTemplateRTView";
import DocCreateTemplateRTView from "../views/DocCreateTemplateRTView";
import DocEditRTView from "../views/DocEditRTView";


// ================================================================================================================== //
// ======================================================= PAGES ==================================================== //
// ================================================================================================================== //

// Authentication Pages
const MaintenancePage = Loadable(lazy(() => import('src/pages/maintenance')));
const DocsPage = Loadable(lazy(() => import('src/pages/docs/DocsPage')));
const TemplatesPage = Loadable(lazy(() => import('src/pages/docs/views/DocTemplateView')));
const Templatesedit = Loadable(lazy(() => import('src/pages/docs/views/templatesedit')));
const DocsListTemplatesRTViewPage = Loadable(lazy(() => import('src/pages/docs/views/DocsListTemplatesRTViewPage')));
const DocPage = Loadable(lazy(() => import('src/pages/doc/PublicDocPage')));
const PagePermissionDenied = Loadable(lazy(() => import('src/pages/access/PagePermissionDenied')));

// ================================================================================================================== //
// ===================================================== ROUTES ===================================================== //
// ================================================================================================================== //

export const deviceRoutes: TypeRouteItem = {
  path: '/',
  element: (
    <AuthGuard path={PATH_APP_AUTH.rootFull}>
      <MainLayout />
    </AuthGuard>
  ),
  children: [
    {
      path: '',
      element: <DocEditView />
    },
  ]
}

/**
 * @description - Bisflow routes list, contains list of RouteItem type
 * objects
 */
const docsRoutes: TypeRouteItem = {
  path: PATH_APP_DOCS.root,
  element: (
    <AuthGuard path={PATH_APP_AUTH.rootFull}>
      <DashboardLayout navConfig={docsNavBarConfig} />
    </AuthGuard>
  ),
  children: [
    { element: <Navigate to={PATH_APP_DOCS.active.contractsFull} replace />, index: true },
    // Dashboard
    {
      path: PATH_APP_DOCS.dashboard,
      element: <MaintenancePage />
    },
    // Process section - REDIRECT -
    {
      path: PATH_APP_DOCS.active.root,
      element: <Navigate to={PATH_APP_DOCS.active.contractsFull} replace />,
      index: true
    },
    // Process section - CONTRACTS -
    {
      path: PATH_APP_DOCS.active.contracts,
      element: <DocsPage page_type={'contract'} page_view={'list'} editable={true} />
    },
    {
      path: 'active/contracts-old/create',
      element: <DocsPage page_type={'contract'} page_view={'create'} editable={false} />
    },
    {
      path: 'active/contracts/create',
      element: <DocCreateTemplateRTView pageType={'rttemplates'} />
    },
    {
      path: 'templatescreate',
      element: <TemplatesPage />
    },
    {
      path: 'templatesedit/:id',
      element: <Templatesedit />
    },
    {
      path: 'templates',
      element: <DocsListTemplatesRTViewPage />
    },
    {
      path: PATH_APP_DOCS.active.contractEdit,
      element: <DocsPage page_type={'contract'} page_view={'edit'} editable={false} />
    },
    {
      path: 'active/contracts-old/:id',
      element: <DocsPage page_type={'contract'} page_view={'view'} editable={false} />
    },
    {
      path: 'active/contracts/:id',
      element: <DocEditRTView />
    },
    // Process section - ACTS -
    {
      path: PATH_APP_DOCS.active.acts,
      element: <DocsPage page_type={'act'} page_view={'list'} editable={true} />
    },
    {
      path: PATH_APP_DOCS.active.actCreate,
      element: <DocsPage page_type={'act'} page_view={'create'} editable={false} />
    },
    {
      path: PATH_APP_DOCS.active.actEdit,
      element: <DocsPage page_type={'act'} page_view={'edit'} editable={false} />
    },
    {
      path: PATH_APP_DOCS.active.act,
      element: <DocsPage page_type={'act'} page_view={'view'} editable={false} />
    },
    // Process section - DOCUMENTS -
    {
      path: PATH_APP_DOCS.active.documents,
      element: <DocsPage page_type={'document'} page_view={'list'} editable={true} />
    },
    {
      path: PATH_APP_DOCS.active.documentCreate,
      element: <DocsPage page_type={'document'} page_view={'create'} editable={false} />
    },
    {
      path: PATH_APP_DOCS.active.documentEdit,
      element: <DocsPage page_type={'document'} page_view={'edit'} editable={false} />
    },
    {
      path: PATH_APP_DOCS.active.document,
      element: <DocsPage page_type={'document'} page_view={'view'} editable={false} />
    },

    // Personnel section - REDIRECT -
    {
      path: PATH_APP_DOCS.archive.root,
      element: <Navigate to={PATH_APP_DOCS.archive.contractsFull} replace />,
      index: true,
    },
    // Personnel section - CONTRACTS -
    {
      path: PATH_APP_DOCS.archive.contracts,
      element: <DocsPage page_type={'contract'} page_view={'list-archive'} editable={true} />
    },
    // Personnel section - ACTS -
    {
      path: PATH_APP_DOCS.archive.acts,
      element: <MaintenancePage />
    },
    // Personnel section - DOCUMENTS -
    {
      path: PATH_APP_DOCS.archive.documents,
      element: <MaintenancePage />
    },

    // Reports section - REDIRECT -
    {
      path: PATH_APP_DOCS.reports.root,
      element: <Navigate to={PATH_APP_DOCS.reports.contractsFull} replace />
    },
    // Reports section - CONTRACTS -
    {
      path: PATH_APP_DOCS.reports.contracts,
      element: <MaintenancePage />
    },
    // Reports section - DOCUMENTS -
    {
      path: PATH_APP_DOCS.reports.documents,
      element: <MaintenancePage />
    },
    // Reports section - ACTS -
    {
      path: PATH_APP_DOCS.reports.acts,
      element: <MaintenancePage />
    },

    // Analytics section - REDIRECT -
    {
      path: PATH_APP_DOCS.analytics.root,
      element: <Navigate to={PATH_APP_DOCS.analytics.contractsFull} replace />
    },
    // Reports section - CONTRACTS -
    {
      path: PATH_APP_DOCS.analytics.contracts,
      element: <MaintenancePage />
    },
    // Reports section - DOCUMENTS -
    {
      path: PATH_APP_DOCS.analytics.documents,
      element: <MaintenancePage />
    },
    // Reports section - ACTS -
    {
      path: PATH_APP_DOCS.analytics.acts,
      element: <MaintenancePage />
    }
  ]
};

export const docRoutes: TypeRouteItem = {
  path: '/',
  element: (<AuthGuard path={PATH_APP_AUTH.rootFull}>
    <MainLayout />
  </AuthGuard>),
  children: [
    { element: <Navigate to={'/apps'} replace />, index: true },
    // Applications
    {
      path: '/apps/:id',
      element: <DocPage />
    },
    { path: '*', element: <PagePermissionDenied to="/404" replace /> },
    // {
    //   path: '*',
    //   element: <PagePermissionDenied />
    // }
  ]
}

export default docsRoutes;
